import React, { useCallback, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import omit from 'lodash/omit';
import { useFragment, graphql } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import messages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import type { sprint_issueFieldSprintReadviewFull_SprintReadView$key as SprintFragment } from '@atlassian/jira-relay/src/__generated__/sprint_issueFieldSprintReadviewFull_SprintReadView.graphql';
import type { ClosedSprint } from '@atlassian/jira-shared-types/src/rest/jira/closed-sprint.tsx';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import {
	useCellHoverHandlers,
	CellHoverProvider,
} from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import type { SprintOption, SprintValue } from '../../common/types.tsx';
import {
	findActiveOrFutureSprint,
	filterClosedSprints,
	getRapidViewUrl,
	isObjectWithSprintState,
} from '../../common/utils.tsx';
import ClosedSprints from './closed-sprints/index.tsx';
import type { Props as ClosedSprintsProps } from './closed-sprints/types.tsx';
import SprintContent from './sprint-content/index.tsx';
import type { SprintReadViewProps } from './types.tsx';

/**
 * The SprintReadView will show a read only view of Sprint field.
 * @param props [SprintReadViewProps](./types.tsx)
 */
export const SprintReadView = ({
	fragmentRef,
	shouldHideSprintLink = false,
	shouldHideClosedSprintsLinks = false,
	placeholder,
	onSprintClick,
	getUrl = getRapidViewUrl,
	isTruncated,
}: SprintReadViewProps) => {
	const { formatDate, formatMessage } = useIntl();
	const placeholderMessage = placeholder || formatMessage(messages.emptyFieldText);

	const { selectedSprintsConnection } = useFragment<SprintFragment>(
		graphql`
			fragment sprint_issueFieldSprintReadviewFull_SprintReadView on JiraSprintField {
				selectedSprintsConnection {
					edges {
						node {
							id
							sprintId
							name @required(action: THROW)
							state @required(action: THROW)
							endDate
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const sprints = useMemo<SprintValue[]>(
		() =>
			selectedSprintsConnection?.edges
				?.filter(Boolean)
				.map(({ node }) => node)
				.filter(Boolean)
				.filter(isObjectWithSprintState)
				.map((node) => ({
					id: node.id,
					sprintId: parseInt(node.sprintId, 10),
					name: node.name,
					state: node.state,
					endDate: node.endDate ?? undefined,
				})) ?? [],
		[selectedSprintsConnection?.edges],
	);

	const value = useMemo<SprintOption>(() => {
		const currentSprint = findActiveOrFutureSprint(sprints);

		if (!currentSprint) return null;

		const sprintOption = {
			label: currentSprint.name,
			value: currentSprint.id,
			href: getUrl(currentSprint.sprintId),
			state: currentSprint.state,
		};

		// If in backlog, use onSprintClick instead of href
		return onSprintClick ? omit(sprintOption, 'href') : sprintOption;
	}, [getUrl, onSprintClick, sprints]);

	const closedSprints = useMemo<ClosedSprint[]>(
		() =>
			filterClosedSprints(sprints)?.map((closedSprint: SprintValue) => ({
				id: closedSprint.sprintId,
				name: closedSprint.name,
				href: getUrl(closedSprint.sprintId),
				endDate: formatDate(closedSprint.endDate, {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
				}),
			})),
		[formatDate, getUrl, sprints],
	);

	const sprintContentRef = useRef(null);

	const renderClosedSprints = useCallback(
		(props: ClosedSprintsProps) => {
			return (
				<CellHoverProvider>
					{closedSprints.length > 0 && <ClosedSprints {...props} />}
				</CellHoverProvider>
			);
		},
		[closedSprints],
	);

	if (isTruncated) {
		// Disabling rule of hooks here to only use the useCellHoverHandlers hook when the single line row height
		// experiment is on. This would help mitigate different issues that may arise with the said experiment.
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { handleMouseEnter, handleMouseLeave } = useCellHoverHandlers();
		const singleLineRowHeightProps = {
			onMouseEnter: handleMouseEnter,
			onFocus: handleMouseEnter,
			onMouseLeave: handleMouseLeave,
			onBlur: handleMouseLeave,
		};

		return (
			<Inline space="space.100" alignBlock="center" shouldWrap={!isTruncated} xcss={inlineStyles}>
				<HoverPopover
					content={
						<SprintContent
							value={value}
							shouldHideSprintLink={shouldHideSprintLink}
							placeholder={placeholderMessage}
							onClick={onSprintClick}
						/>
					}
					contentRef={sprintContentRef}
					shouldGetParentRef
				>
					{({ ref }) => (
						<SprintContent
							value={value}
							shouldHideSprintLink={shouldHideSprintLink}
							placeholder={placeholderMessage}
							onClick={onSprintClick}
							sprintContentRef={mergeRefs(ref, sprintContentRef)}
							hoverAttributes={singleLineRowHeightProps}
						/>
					)}
				</HoverPopover>
				{renderClosedSprints({
					closedSprints,
					shouldHideLinks: shouldHideClosedSprintsLinks,
					isTruncated: true,
				})}
			</Inline>
		);
	}

	return (
		<ReadWrapper>
			<SprintContent
				value={value}
				shouldHideSprintLink={shouldHideSprintLink}
				placeholder={placeholderMessage}
				onClick={onSprintClick}
			/>

			{renderClosedSprints({ closedSprints, shouldHideLinks: shouldHideClosedSprintsLinks })}
		</ReadWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	flexWrap: 'wrap',
	gap: token('space.100'),
});

const inlineStyles = xcss({
	overflow: 'hidden',
});
